import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    CAApi,
    CAKeyUsageFilter,
    CertificateStore,
    CertificateValidationApi,
    type CertificateValidationResult,
    PropertyApi,
    X509CertificateDetails
} from "components/src/api/admin/generated";
import {CertificateRow} from "components/src/certificate/CertificateRow";
import {CertificateTableHeader} from "components/src/certificate/CertificateTableHeader";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react'
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const caApi = new CAApi(ApiConfig.config());
const certificateValidationApi = new CertificateValidationApi(ApiConfig.config());
const propertyApi = new PropertyApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface SelectCADialogProps {
    onClose : OnClose
}

export const SelectCADialog = ({onClose} : SelectCADialogProps) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * All the CA certificates
     */
    const [authorities, setAuthorities] = useState<X509CertificateDetails[]>([]);
    /**
     * The active CA certificate
     */
    const [activeThumbprint, setActiveThumbprint] = useState<string>();
    /**
     * The selected CA certificate
     */
    const [selectedThumbprint, setSelectedThumbprint] = useState<string>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while saving the selected certificate to show a spinner on the import button
     */
    const [saving, setSaving] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * Load all CA certificates
     */
    useEffect(() =>
    {
        logger.debug("Getting local CA certificates");

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        caApi.cAControllerGetAvailableCAs(CAKeyUsageFilter.All)
            .then(response => {
                setAuthorities(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting local CA certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    /**
     * Load currently selected CA
     */
    useEffect(() =>
    {
        logger.debug("Getting selected local CA certificates");

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        propertyApi.propertyControllerGetGlobalProperty("ca-issuer-thumbprint").then((result) => {
            setActiveThumbprint(result.data.value);
            setSelectedThumbprint(result.data.value);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting CA certificate", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    /**
     * Checks the validity of a certificate using the certificate validation API.
     *
     * NOTE: this must be wrapped in a useCallback call because the method is provided to CertificateRow
     */
    const checkCertificateValidity = (useCallback((certificate : X509CertificateDetails, setCertificateValidationResult : Dispatch<SetStateAction<CertificateValidationResult | undefined>>) =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        certificateValidationApi.certificateValidationControllerValidateCertificate(CertificateStore.Certificates, certificate.thumbprint)
            .then((response) =>
            {
                const validationResult : CertificateValidationResult = response.data;

                setCertificateValidationResult(validationResult);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error certificate validation result", err);
                setCertificateValidationResult(undefined);
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter]));

    /**
     * Called when a certificate is selected (i.e., when a radio button is selected)
     */
    const onSelectCertificate = (thumbprint : string) => {
        setSelectedThumbprint(thumbprint);
    }

    const save = () =>
    {
        setSaving(true);

        propertyApi.propertyControllerSetGlobalProperty("ca-issuer-thumbprint", selectedThumbprint).then(() => {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error saving CA certificate", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("ca.local-cas")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>

                <Table bordered hover responsive>
                    <CertificateTableHeader/>
                    <tbody>
                    {authorities.map((certificate) => (
                        <CertificateRow key={certificate.thumbprint} certificate={certificate} checkCertificateValidityCallback={checkCertificateValidity}>
                            {/* Add radio button to first column */}
                            <Form.Check
                                name="signing-certificate"
                                type="radio"
                                value={certificate.thumbprint}
                                checked={selectedThumbprint === certificate.thumbprint}
                                onChange={(e) => onSelectCertificate(e.target.value)}
                                inline
                            />
                        </CertificateRow>
                    ))}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={save}
                        disabled={activeThumbprint === selectedThumbprint}>
                    {saving && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {saving ? t("common.saving") : t("common.save")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
